import {
  FocusEvent as ReactFocusEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { SuggestingTextField } from '@ui2/forms'
import { MaterialSymbol } from '@ui2/icons'
import { useSnackbar } from '@ui/snackbar'
import { useResultsStore } from '@editorStores'
import { useBlockAction } from '@editorHooks'
import { Results2ElementListFormValues } from '..'
import {
  ANCHOR_COMMENT_SUGGESTIONS,
  PREAMBLE_COMMENT_SUGGESTIONS,
  POSITION_LIST_COMMENT_SUGGESTIONS,
  withUserCommentSuggestion,
} from '../../../commentSuggestions'
import { isUtilizationError, isUtilizationWarning } from '../../../utils'
import {
  RESULTS2_DESIGN_FORM_BLOCK_ACTION,
  UNSAVED_BEMESSUNG_NAV_MESSAGE,
  unsavedBemessungNavSnackbarOptions,
} from '../../Results2DetailPane'
import type { DropKind, Results2ListItem } from '../types'
import { EtaStack } from './EtaStack'
import { RowMetric } from './RowMetric'

/** Hide Vorbemerkungen column when the row is narrower than this (list pane resize). */
const EXTENDED_COMMENT_COLUMN_MIN_ROW_WIDTH_PX = 450

/** Compact list — cross-section / assembly subtitle column. */
const QS_LABEL_MAX_WIDTH_PX = 70
const QS_LABEL_PART_SEPARATOR = ' · '
const QS_LABEL_MAX_PARTS = 1

/**
 * Width of the first column (label · cross-section · utilization) for lists that show the comment
 * column (Rippen, Stürze, Deckenbalken …). A fixed width keeps every row's first part identically
 * sized so the comment column lines up, while `cqi` lets it grow/shrink with the list pane — the row
 * is an inline-size container — so long cross-sections (Deckenbalken) become readable as the pane is
 * widened. Lists without a comment column (Standardrippen, Wandscheiben) keep their content-sized
 * first column so their assembly caption can stretch freely.
 */
const FIRST_COLUMN_RESPONSIVE_WIDTH = 'clamp(140px, 34cqi, 360px)'
/**
 * Lists with a single comment sub-column (anchors) hold label · η · F_d,neu in the first column.
 * It is kept just wide enough for those three so the values sit right after the label instead of
 * being pushed to the right edge, and barely grows with the pane — the width won on a wider list
 * belongs to the comment, which is the column that benefits from it.
 */
const FIRST_COLUMN_SINGLE_COMMENT_RESPONSIVE_WIDTH = 'clamp(248px, 30cqi, 328px)'

const truncateQsLabel = (qs: string): string => {
  const parts = qs.split(QS_LABEL_PART_SEPARATOR)
  if (parts.length <= QS_LABEL_MAX_PARTS) return qs
  return `${parts.slice(0, QS_LABEL_MAX_PARTS).join(QS_LABEL_PART_SEPARATOR)}…`
}

/** Compact row actions — MUI IconButton defaults to minWidth 34px otherwise. */
const compactRowIconButtonSx = {
  p: 0,
  width: 16,
  height: 16,
  minWidth: 16,
  minHeight: 16,
  borderRadius: '50%',
} as const

const compactRowIndicatorSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  flexShrink: 0,
} as const

/** Comment field wrapper — stretches to the full list row height. */
const compactListCommentFieldContainerSx = {
  flex: 1,
  minWidth: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiAutocomplete-root': {
    flex: 1,
    minHeight: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .MuiFormControl-root': {
    flex: 1,
    minHeight: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
} as const

/** Single-line comment fields sized to fit the fixed virtual list row. */
const compactListCommentTextFieldSx = {
  flex: 1,
  minWidth: 0,
  height: '100%',
  alignSelf: 'stretch',
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  '& .MuiAutocomplete-inputRoot': {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    minHeight: 0,
    height: '100%',
    flex: 1,
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-root': {
    fontFamily: 'Roboto',
    fontSize: 12,
    lineHeight: 1.2,
    minHeight: 0,
    height: '100%',
    flex: 1,
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-input': {
    padding: '0 !important',
    minHeight: 0,
  },
  '& .MuiInputBase-inputMultiline': {
    padding: '0 !important',
    height: '100% !important',
    minHeight: '100% !important',
    maxHeight: '100%',
    overflow: 'auto !important',
    boxSizing: 'border-box',
    resize: 'none',
    verticalAlign: 'top',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0,0,0,0.28) transparent',
    '&::-webkit-scrollbar': {
      width: 4,
      height: 4,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,0.28)',
      borderRadius: 999,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(0,0,0,0.4)',
    },
  },
} as const

const compactListReadonlyCommentSx = {
  ...compactListCommentFieldContainerSx,
  width: '100%',
  alignSelf: 'stretch',
  margin: 0,
  fontFamily: 'Roboto',
  fontSize: 12,
  lineHeight: 1.2,
  whiteSpace: 'pre-wrap',
  overflow: 'hidden',
  boxSizing: 'border-box',
} as const

export type ListRowProps = {
  /** Position of the row's item in the form `items` array (field path `items.${index}`). */
  index: number
  depth: number
  isParent: boolean
  open: boolean
  onToggle?: () => void
  selected: boolean
  showHandle: boolean
  /** When grouping uses tree mode elsewhere, reserve the drag/chevron column so flat sorts align. */
  reserveHandleColumn?: boolean
  onClick: (event: ReactMouseEvent<HTMLElement>) => void
  dragging?: boolean
  dropIndicator?: DropKind | null
  onPointerDragStart?: (e: ReactPointerEvent<HTMLElement>) => void
  onCopyParentCrossSection?: () => void
  copyParentCrossSectionLoading?: boolean
  showAddToGroupButton?: boolean
  onAddToGroup?: () => void
  /** Compact lists (Standardrippen, Wandscheiben): caption instead of exportiert. */
  hideExportButton?: boolean
  /**
   * When false (sorted flat modes), rows ignore parent/child grouping for layout and actions —
   * every row renders like a top-level element.
   */
  hierarchyDisplay?: boolean
  disableForm?: boolean
  showCommentColumn?: boolean
  /** When false, hides the Vorbemerkungen sub-column (lists whose items only have one comment). */
  showExtendedCommentColumn?: boolean
  /** Lets the `qs` line use the whole first column instead of the compact cross-section width. */
  qsFullWidth?: boolean
  /** Placeholder of the comment column field (default "Positionsliste..."). */
  commentPlaceholder?: string
  /** Which suggestion pool the comment field offers — anchors keep their own (default position). */
  commentSuggestionKind?: 'position' | 'anchor'
}

export const ListRow = ({
  index,
  depth,
  isParent,
  open,
  onToggle,
  selected,
  showHandle,
  reserveHandleColumn = false,
  onClick,

  dragging = false,
  dropIndicator = null,
  onPointerDragStart,
  onCopyParentCrossSection,
  copyParentCrossSectionLoading = false,
  showAddToGroupButton = false,
  onAddToGroup,
  hideExportButton = false,
  hierarchyDisplay = true,
  disableForm = false,
  showCommentColumn = true,
  showExtendedCommentColumn = true,
  qsFullWidth = false,
  commentPlaceholder = 'Positionsliste...',
  commentSuggestionKind = 'position',
}: ListRowProps): ReactElement => {
  const rowBaseField = `items.${index}`
  const row = useWatch<Results2ElementListFormValues>({
    name: rowBaseField as `items.${number}`,
  }) as Results2ListItem

  const theme = useTheme()
  const eta = row.eta
  const etaC = row.etaC
  const etaOk = eta !== undefined && Number.isFinite(eta)
  const etaCOk = etaC !== undefined && Number.isFinite(etaC)
  const isOver =
    (etaOk && isUtilizationError(eta, row.etaAllowedOverutilizationPercent)) ||
    (etaCOk && isUtilizationError(etaC as number, row.etaCAllowedOverutilizationPercent))
  const isNear =
    !isOver &&
    ((etaOk && isUtilizationWarning(eta, row.etaAllowedOverutilizationPercent)) ||
      (etaCOk && isUtilizationWarning(etaC as number, row.etaCAllowedOverutilizationPercent)))
  const hasComment = (row.comment ?? '').trim().length > 0
  const hasExtendedComment = (row.extended_comment ?? '').trim().length > 0

  const isChild = hierarchyDisplay && (depth > 0 || !!row.parent)

  const borderLeftColor = selected
    ? theme.palette.primary.main
    : isOver
    ? theme.palette.warning.main
    : isNear
    ? '#B28100'
    : 'transparent'

  const background =
    dropIndicator === 'onto'
      ? 'rgba(55,132,52,0.18)'
      : selected
      ? 'rgba(55,132,52,0.08)'
      : depth > 0
      ? 'rgba(0,0,0,0.045)'
      : 'transparent'

  const { setValue } = useFormContext()
  const { enqueueSnackbar } = useSnackbar()
  const { isBlocked: isDesignFormBlocked, isBlockedFn: isDesignFormBlockedFn } = useBlockAction(
    RESULTS2_DESIGN_FORM_BLOCK_ACTION,
    'subscriber',
  )
  // The anchor detail pane edits the selected anchor's comment too and only saves it explicitly —
  // while it holds unsaved edits, the list must not autosave a comment the pane would overwrite.
  const commentFieldsDisabled =
    disableForm ||
    !!row.parent ||
    (commentSuggestionKind === 'anchor' && selected && isDesignFormBlocked)
  const positionCommentSuggestion = useResultsStore(state => state.commentSuggestion)
  const anchorCommentSuggestion = useResultsStore(state => state.anchorCommentSuggestion)
  const extendedCommentSuggestion = useResultsStore(state => state.extendedCommentSuggestion)
  const setPositionCommentSuggestion = useResultsStore(state => state.setCommentSuggestion)
  const setAnchorCommentSuggestion = useResultsStore(state => state.setAnchorCommentSuggestion)
  const setExtendedCommentSuggestion = useResultsStore(state => state.setExtendedCommentSuggestion)

  const isAnchorSuggestions = commentSuggestionKind === 'anchor'
  const commentSuggestion = isAnchorSuggestions
    ? anchorCommentSuggestion
    : positionCommentSuggestion
  const setCommentSuggestion = isAnchorSuggestions
    ? setAnchorCommentSuggestion
    : setPositionCommentSuggestion

  const commentSuggestions = useMemo(
    () =>
      withUserCommentSuggestion(
        isAnchorSuggestions ? ANCHOR_COMMENT_SUGGESTIONS : POSITION_LIST_COMMENT_SUGGESTIONS,
        commentSuggestion,
      ),
    [isAnchorSuggestions, commentSuggestion],
  )
  const extendedCommentSuggestions = useMemo(
    () => withUserCommentSuggestion(PREAMBLE_COMMENT_SUGGESTIONS, extendedCommentSuggestion),
    [extendedCommentSuggestion],
  )

  const notifyUnsavedDesignChanges = useCallback(() => {
    enqueueSnackbar(UNSAVED_BEMESSUNG_NAV_MESSAGE, unsavedBemessungNavSnackbarOptions)
  }, [enqueueSnackbar])

  const blockCommentFieldWhenUnsaved = useCallback((): boolean => {
    if (!isDesignFormBlockedFn()) return false
    notifyUnsavedDesignChanges()
    return true
  }, [isDesignFormBlockedFn, notifyUnsavedDesignChanges])

  const canOpenCommentSuggestions = useCallback(
    () => !isDesignFormBlockedFn(),
    [isDesignFormBlockedFn],
  )

  const [isDirty, setIsDirty] = useState(false)

  const handlePositionListCommentBlur = useCallback(
    (value: string) => {
      if (!isDirty) return
      setCommentSuggestion(value.trim())
    },
    [isDirty, setCommentSuggestion],
  )

  const onUngroup = useCallback(() => {
    setValue(`${rowBaseField}.parent`, undefined, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [setValue, rowBaseField])

  const handleCommentFieldMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      event.stopPropagation()
      if (blockCommentFieldWhenUnsaved()) {
        event.preventDefault()
      }
    },
    [blockCommentFieldWhenUnsaved],
  )

  const handleCommentFieldFocus = useCallback(
    (event: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      event.stopPropagation()
      if (blockCommentFieldWhenUnsaved()) {
        event.target.blur()
        return
      }
      onClick({
        metaKey: false,
        ctrlKey: false,
        shiftKey: false,
      } as ReactMouseEvent<HTMLElement>)
    },
    [blockCommentFieldWhenUnsaved, onClick],
  )

  const onToggleExported = useCallback(() => {
    setValue(`${rowBaseField}.ok`, !row.ok, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [setValue, rowBaseField, row.ok])

  const renderReadonlyComment = (value: string | undefined, fallback: string): ReactElement => {
    const text = value?.trim() || fallback

    return (
      <Box pt={0} flex={1}>
        <Typography
          component="div"
          sx={{
            ...compactListReadonlyCommentSx,
            color: theme.palette.text.disabled,
            cursor: 'default',
          }}
        >
          {text}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      data-row-id={row.id}
      data-is-parent={isParent ? 'true' : 'false'}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        containerType: 'inline-size',
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${borderLeftColor}`,
        background,
        opacity: dragging ? 0.4 : 1,
        transition: 'background 120ms',
        '&:hover': {
          background:
            dropIndicator === 'onto'
              ? 'rgba(55,132,52,0.18)'
              : selected
              ? 'rgba(55,132,52,0.08)'
              : depth > 0
              ? 'rgba(0,0,0,0.065)'
              : theme.palette.action.hover,
        },
      }}
    >
      {dropIndicator === 'before' ? (
        <Box
          sx={{
            position: 'absolute',
            top: -1,
            left: 0,
            right: 0,
            height: 2,
            background: theme.palette.primary.main,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      {dropIndicator === 'after' ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: -1,
            left: 0,
            right: 0,
            height: 2,
            background: theme.palette.primary.main,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <Box
        onClick={onClick}
        data-cy="results2-list-row-select"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.25,
          py: 0.6,
          pl: hierarchyDisplay ? (isChild ? 1.5 : '2px') : 1,
          cursor: 'pointer',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          // Prevent native text highlighting when shift/ctrl-clicking to multi-select rows.
          userSelect: 'none',
        }}
      >
        {showHandle ? (
          isParent ? (
            <Box
              component="span"
              onClick={e => {
                e.stopPropagation()
                onToggle?.()
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 18,
                flexShrink: 0,
                alignSelf: 'center',
                cursor: 'pointer',
                color: theme.palette.text.secondary,
              }}
              title={open ? 'Gruppe einklappen' : 'Gruppe ausklappen'}
            >
              <MaterialSymbol icon={open ? 'expand_more' : 'chevron_right'} size={18} />
            </Box>
          ) : depth > 0 ? (
            <Box
              component="span"
              onPointerDown={onPointerDragStart}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              title="Ziehen zum Verschieben"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                flexShrink: 0,
                alignSelf: 'stretch',
                cursor: dragging ? 'grabbing' : 'grab',
                color: 'rgba(0,0,0,0.18)',
                touchAction: 'none',
                '&:hover': {
                  color: theme.palette.text.secondary,
                },
              }}
            >
              <MaterialSymbol icon="drag_indicator" size={16} />
            </Box>
          ) : (
            <Box
              component="span"
              onPointerDown={onPointerDragStart}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              title="Ziehen zum Verschieben"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                flexShrink: 0,
                alignSelf: 'center',
                cursor: dragging ? 'grabbing' : 'grab',
                color: 'rgba(0,0,0,0.18)',
                touchAction: 'none',
                '&:hover': {
                  color: theme.palette.text.secondary,
                },
              }}
            >
              <MaterialSymbol icon="drag_indicator" size={16} />
            </Box>
          )
        ) : reserveHandleColumn ? (
          <Box
            aria-hidden
            sx={{
              width: 24,
              flexShrink: 0,
              alignSelf: isChild ? 'stretch' : 'center',
            }}
          />
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            flexShrink: 0,
            ...(showCommentColumn
              ? {
                  width: showExtendedCommentColumn
                    ? FIRST_COLUMN_RESPONSIVE_WIDTH
                    : FIRST_COLUMN_SINGLE_COMMENT_RESPONSIVE_WIDTH,
                  minWidth: 0,
                }
              : // Without the comment columns the label column takes the full row width, so the
                // η stack right-aligns into one column instead of trailing the variable-width label.
                { flex: 1, minWidth: 70 }),
          }}
        >
          <Stack direction="row" alignItems="baseline" gap={0.5}>
            <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.25}>
              <Box
                data-cy="results2-list-row-label"
                sx={{
                  fontSize: 13,
                  fontWeight: isParent ? 600 : isChild ? 400 : 500,
                  color: theme.palette.text.primary,
                  fontFamily: 'Roboto',
                  lineHeight: 1.2,
                  // Truncate instead of overflowing onto the category/η fields when the list narrows.
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.label ?? row.id}
              </Box>
              <Tooltip
                title={
                  row.qsDiffersFromParent
                    ? `Querschnitt weicht von der Gruppe ab: ${row.qs}`
                    : row.qsManual
                    ? `${row.qs} (manuell festgelegt)`
                    : row.qs
                }
                arrow
                disableInteractive
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.2,
                    display: 'block',
                    maxWidth: showCommentColumn || qsFullWidth ? '100%' : QS_LABEL_MAX_WIDTH_PX,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      ...(row.qsDiffersFromParent
                        ? {
                            px: 0.25,
                            borderRadius: 0.25,
                            backgroundColor: '#FFF1C9',
                            color: '#7A5A00',
                          }
                        : {
                            color: theme.palette.text.secondary,
                          }),
                    }}
                  >
                    {truncateQsLabel(row.qs)}
                  </Box>
                  {row.qsManual && (
                    <Box
                      component="span"
                      sx={{ ml: 0.5, color: theme.palette.text.secondary, fontStyle: 'italic' }}
                      data-cy="anchor-manual-marker"
                    >
                      manuell
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Stack>
            <EtaStack
              eta={row.eta}
              etaC={row.etaC}
              etaShort={row.etaShort}
              etaShortTooltip={row.etaShortTooltip}
              etaAllowedOverutilizationPercent={row.etaAllowedOverutilizationPercent}
              etaCAllowedOverutilizationPercent={row.etaCAllowedOverutilizationPercent}
              hideUnavailable={row.etaNotApplicable}
              etaIsError={row.etaIsVibrationError}
            />
            {row.trailingMetric ? (
              <RowMetric
                value={row.trailingMetric.value}
                unit={row.trailingMetric.unit}
                tooltip={row.trailingMetric.tooltip}
                highlight={row.trailingMetric.highlight}
              />
            ) : null}
          </Stack>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              minHeight: 14,
            }}
          >
            {isChild ? (
              <>
                <Tooltip title="Aus Gruppe entfernen" arrow disableInteractive>
                  <IconButton
                    size="small"
                    disabled={disableForm}
                    onClick={e => {
                      e.stopPropagation()
                      if (disableForm) return
                      onUngroup?.()
                    }}
                    onMouseDown={e => e.stopPropagation()}
                    sx={{
                      ...compactRowIconButtonSx,
                      color: 'rgba(0,0,0,0.28)',
                      '&:hover': {
                        background: 'rgba(0,0,0,0.06)',
                        color: theme.palette.text.secondary,
                      },
                    }}
                  >
                    <MaterialSymbol icon="playlist_remove" size={13} />
                  </IconButton>
                </Tooltip>
                {onCopyParentCrossSection ? (
                  <Tooltip
                    title={
                      copyParentCrossSectionLoading
                        ? 'Querschnitt wird übernommen…'
                        : 'Querschnitt der Gruppe übernehmen'
                    }
                    arrow
                    disableInteractive
                  >
                    {copyParentCrossSectionLoading ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 16,
                          height: 16,
                          flexShrink: 0,
                        }}
                      >
                        <CircularProgress
                          size={10}
                          thickness={5}
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </Box>
                    ) : (
                      <IconButton
                        size="small"
                        disabled={disableForm}
                        onClick={e => {
                          e.stopPropagation()
                          if (disableForm) return
                          onCopyParentCrossSection()
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        sx={{
                          ...compactRowIconButtonSx,
                          color: 'rgba(0,0,0,0.28)',
                          '&:hover': {
                            background: 'rgba(0,0,0,0.06)',
                            color: theme.palette.text.secondary,
                          },
                        }}
                      >
                        <MaterialSymbol icon="content_copy" size={13} />
                      </IconButton>
                    )}
                  </Tooltip>
                ) : null}
              </>
            ) : hideExportButton ? (
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  lineHeight: 1.2,
                  color: theme.palette.text.secondary,
                  fontFamily: 'Roboto',
                  pr: 0.25,
                }}
              >
                {row.assemblyName ?? ''}
              </Box>
            ) : (
              <Tooltip
                title={
                  row.ok ? 'Exportiert — klicken zum Zurücksetzen' : 'Als exportiert markieren'
                }
                arrow
                disableInteractive
              >
                <IconButton
                  size="small"
                  disabled={disableForm}
                  onClick={e => {
                    e.stopPropagation()
                    if (disableForm) return
                    onToggleExported?.()
                  }}
                  onMouseDown={e => e.stopPropagation()}
                  sx={{
                    ...compactRowIconButtonSx,
                  }}
                >
                  <MaterialSymbol
                    icon="check_circle"
                    filled={row.ok}
                    size={13}
                    color={row.ok ? theme.palette.success.main : 'rgba(0,0,0,0.28)'}
                  />
                </IconButton>
              </Tooltip>
            )}
            {showAddToGroupButton ? (
              <Tooltip title="Zur ausgewählten Gruppe hinzufügen" arrow disableInteractive>
                <IconButton
                  size="small"
                  disabled={disableForm}
                  onClick={e => {
                    e.stopPropagation()
                    if (disableForm) return
                    onAddToGroup?.()
                  }}
                  onMouseDown={e => e.stopPropagation()}
                  sx={{
                    ...compactRowIconButtonSx,
                    color: 'rgba(0,0,0,0.28)',
                    '&:hover': {
                      background: 'rgba(0,0,0,0.06)',
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  <MaterialSymbol icon="group_work" size={13} color={theme.palette.primary.main} />
                </IconButton>
              </Tooltip>
            ) : null}
            {hasComment || hasExtendedComment ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {hasComment ? (
                  <Tooltip title="Kommentar vorhanden" arrow disableInteractive>
                    <Box component="span" sx={compactRowIndicatorSx}>
                      <MaterialSymbol
                        icon="chat_bubble"
                        size={12}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    </Box>
                  </Tooltip>
                ) : null}
                {hasExtendedComment ? (
                  <Tooltip title="Vorbemerkungen vorhanden" arrow disableInteractive>
                    <Box component="span" sx={compactRowIndicatorSx}>
                      <MaterialSymbol
                        icon="sticky_note_2"
                        size={12}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    </Box>
                  </Tooltip>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            // Without the comment columns this container is empty and must not grow, so the
            // label column (and its right-aligned η stack) can extend to the row's right edge.
            flex: showCommentColumn ? 1 : '0 0 auto',
            minWidth: 0,
            minHeight: 0,
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'stretch',
            overflow: 'hidden',
          }}
        >
          {showCommentColumn ? (
            <Stack
              direction="row"
              gap={1}
              flex={1}
              minWidth={0}
              height="100%"
              alignSelf="stretch"
              alignItems="stretch"
              justifyContent="space-evenly"
              sx={{ userSelect: 'text' }}
            >
              {commentFieldsDisabled ? (
                renderReadonlyComment(row.comment, !row.parent ? commentPlaceholder : '')
              ) : (
                <SuggestingTextField
                  name={`${rowBaseField}.comment`}
                  suggestions={commentSuggestions}
                  placeholder={!row.parent ? commentPlaceholder : ''}
                  disabled={false}
                  minRows={1}
                  maxRows={4}
                  variant="standard"
                  openOnFocus
                  canOpen={canOpenCommentSuggestions}
                  onFieldBlur={handlePositionListCommentBlur}
                  containerSx={compactListCommentFieldContainerSx}
                  textFieldSx={compactListCommentTextFieldSx}
                  InputProps={{ disableUnderline: true }}
                  onClick={e => e.stopPropagation()}
                  onMouseDown={handleCommentFieldMouseDown}
                  onFocus={handleCommentFieldFocus}
                  onChangeText={() => setIsDirty(true)}
                />
              )}
              {showExtendedCommentColumn ? (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: 'none',
                    overflow: 'hidden',
                    [`@container (min-width: ${EXTENDED_COMMENT_COLUMN_MIN_ROW_WIDTH_PX}px)`]: {
                      display: 'flex',
                      height: '100%',
                      alignItems: 'stretch',
                    },
                  }}
                >
                  {commentFieldsDisabled ? (
                    renderReadonlyComment(
                      row.extended_comment,
                      !row.parent ? 'Vorbemerkungen...' : '-',
                    )
                  ) : (
                    <SuggestingTextField
                      name={`${rowBaseField}.extended_comment`}
                      suggestions={extendedCommentSuggestions}
                      placeholder={!row.parent ? 'Vorbemerkungen...' : '-'}
                      disabled={false}
                      minRows={1}
                      maxRows={4}
                      variant="standard"
                      openOnFocus
                      canOpen={canOpenCommentSuggestions}
                      onFieldBlur={value => setExtendedCommentSuggestion(value.trim())}
                      containerSx={compactListCommentFieldContainerSx}
                      textFieldSx={compactListCommentTextFieldSx}
                      InputProps={{ disableUnderline: true }}
                      onClick={e => e.stopPropagation()}
                      onMouseDown={handleCommentFieldMouseDown}
                      onFocus={handleCommentFieldFocus}
                    />
                  )}
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}
